import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import Logger from '@/lib/logger';
import type { ValuesType } from '@/components/atoms/Input/InputPropsType';

export function slugToTitle(slug: string) {
  return slug
    .split('-')
    .map((word) =>
      word.length <= 3
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

export function stringToSlug(text: string) {
  return text
    .toString() // Cast to string (optional)
    .normalize('NFKD') // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
    .toLowerCase() // Convert the string to lowercase letters
    .trim() // Remove whitespace from both sides of a string (optional)
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/_/g, '-') // Replace _ with -
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/-$/g, ''); // Remove trailing -
}

export function formatBytes({
  bytes,
  decimals = 2,
  withSuffix = false,
}: {
  bytes: number;
  decimals?: number;
  withSuffix?: boolean;
}) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${withSuffix ? ` ${sizes[i]}` : ''}`;
}

export function getNodeText(node: ReactNode | ReactNode[]): string {
  if (node == null) return '';

  switch (typeof node) {
    case 'string':
    case 'number':
      return node.toString();

    case 'boolean':
      return '';

    case 'object': {
      if (node instanceof Array) return node.map(getNodeText).join('');

      // @ts-expect-error typing not strict here
      if ('props' in node) return getNodeText(node.props.children);

      Logger.warn(
        'Unresolved `node` of type:',
        typeof node,
        node,
        'getNodesText'
      );
      return '';
    }

    default:
      Logger.warn(
        'Unresolved `node` of type:',
        typeof node,
        node,
        'getNodesText'
      );
      return '';
  }
}

/**
 * This function is mainly to format phone numbers that users input
 */
export function formatInputPhoneNumber(phoneNumber: string) {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{1,2}?)(\d{0,3}?)?(\d{0,3}?)?(\d{0,4}?)$/);
  if (match) {
    const intlCode = match[1] ? `+${match[1]} ` : '';
    const areaCode = match[2] ? `(${match[2]}) ` : '';
    const numberPrefix = match[3] ? `${match[3]}-` : '';
    const numberSuffix = match[4] || '';
    return [intlCode, areaCode, numberPrefix, numberSuffix].join('');
  }
  return phoneNumber;
}

export function formatDate(
  date: string | Date,
  defaultValueOnInvalidDate?: string
) {
  const dateWrapper = typeof date === 'string' ? new Date(date) : date;

  if (!dateWrapper) {
    return defaultValueOnInvalidDate;
  }

  return dayjs(dateWrapper).format('dddd, MMMM D, YYYY');
}

export function generateSearchParams(
  params: Record<string, string | number | undefined | null>
) {
  const searchParams = {
    ...params,
  };

  for (const param in searchParams) {
    /* You can get copy by spreading {...query} */
    if (
      searchParams[param] === undefined /* In case of undefined assignment */ ||
      searchParams[param] === null ||
      searchParams[param] === ''
    ) {
      delete searchParams[param];
    }

    if (
      typeof searchParams[param] !== 'string' &&
      typeof searchParams[param] !== 'undefined'
    ) {
      searchParams[param] = String(searchParams[param]);
    }
  }

  return new URLSearchParams(searchParams as Record<string, string>);
}

export interface JWTPayload {
  aud: string;
  iss: string;
  iat: number;
  nbf: number;
  exp: number;
  aio: string;
  name: string;
  nonce: string;
  oid: string;
  preferred_username: string;
  rh: string;
  roles: string[];
  sub: string;
  tid: string;
  uti: string;
  ver: string;
}

export function parseJwt(token: string): JWTPayload | null {
  if (token && token.length > 0) {
    try {
      const base64Url = token.split('.')[1];
      if (base64Url) {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(function (c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
        );

        return JSON.parse(jsonPayload);
      }
    } catch (e) {
      Logger.error(e, 'parseJwt');
    }
  }

  return null;
}

export function isProtocolUrl(url: string) {
  const protocolRegex = /^[a-zA-Z]+:\/\//; // Matches URLs with '://'
  const simpleProtocolRegex = /^[a-zA-Z]+:/; // Matches protocols like 'tel:', 'mailto:'
  return (
    url.startsWith('data:') ||
    protocolRegex.test(url) ||
    simpleProtocolRegex.test(url)
  );
}

export function isNonEmptyArrayOfStrings(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === 'string')
  );
}

export function formatValueToUppercase(value: ValuesType) {
  if (typeof value === 'string') {
    return value.toLocaleUpperCase();
  }

  return value;
}

export function generateUNSECUREJWT({
  preferred_username,
  roles,
}: {
  preferred_username: string;
  roles: string[];
}): string {
  const header = {
    alg: 'HS256', // Algorithm, e.g., HMAC SHA256
    typ: 'JWT', // Type, always JWT
  };

  const payload = {
    preferred_username,
    roles,
    exp: Math.floor(Date.now() / 1000) + 60 * 60, // Expiration time (1 hour from now)
  };

  const encodedHeader = btoa(JSON.stringify(header))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, ''); // Base64url encode

  const encodedPayload = btoa(JSON.stringify(payload))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, ''); // Base64url encode

  return `${encodedHeader}.${encodedPayload}.MOCK_SIGNATURE`; // Placeholder signature
}

export function isFalsy(value: unknown, ignoreZero: boolean = true): boolean {
  if (
    value === null ||
    value === undefined ||
    value === false ||
    Number.isNaN(value)
  ) {
    return true;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  if (typeof value === 'number') {
    return value === 0 ? !ignoreZero : false;
  }

  return false;
}
